/**
 * Login del gestionale partner: autentica, carica employee + partner, popola lo
 * store e decide la pagina di atterraggio in base a ruolo e tipo di partner.
 *
 * Ruoli accettati: employee, admin (ex adminEmployee), salesvendor.
 * Errori: lancia un Error con .code:
 *   'INVALID_CREDENTIALS' | 'NOT_AUTHORIZED' | 'NETWORK' | 'UNKNOWN'
 */
import { toRaw } from 'vue';
import authAPI from './authAPI';
import employeeAPI from './employeeAPI';
import partnerAPI from './partnerAPI';
import { isLoggedStore } from '../store/logged';
import { applyTokens, clearSession, isPartnerAdmin, ROLES } from './session';

/**
 * Rotta di atterraggio per (ruolo, partner). null = combinazione non ammessa.
 * Regole invariate rispetto al vecchio LoginPage, con 'admin' equiparato ad 'adminEmployee'.
 */
export function landingRouteFor(role, partner) {
  const booking = partner?.booking_type === true;
  const journey = partner?.journey_type === true;

  // ⚠️ Questa funzione non sceglie solo DOVE atterrare: `null` significa
  // «combinazione non ammessa» e il login viene RIFIUTATO.
  //
  // I tre ruoli del partner vedono le stesse pagine (nelle Tariffe
  // l'amministratore vede e modifica di più), quindi atterrano tutti sulla
  // Home, le corse di oggi.
  //
  // Il vincolo sulle capability resta: un partner senza servizi è una
  // configurazione incompleta, e non entra nessuno. La regola combacia con le
  // voci di menu in App.vue: se il ruolo ha almeno una voce, deve poter
  // entrare.
  const ruoloPartner =
    role === ROLES.EMPLOYEE || role === ROLES.SALESVENDOR || isPartnerAdmin(role);
  if (!ruoloPartner) return null;
  return booking || journey ? '/' : null;
}

function authError(code, cause) {
  const e = new Error(code);
  e.code = code;
  e.isAuthError = true; // AxiosError ha già un .code (ERR_BAD_REQUEST...): serve un marker nostro
  e.cause = cause;
  return e;
}

/**
 * Esegue il login completo. Ritorna { role, partner, landing }.
 * In caso di errore la sessione viene azzerata.
 */
export async function login({ username, password }) {
  const store = isLoggedStore();
  try {
    const res = await authAPI.login({ username, password });
    const auth = applyTokens(res.data);
    if (!auth?.user) throw authError('UNKNOWN');

    store.id = auth.sub;
    store.mail = auth.user.mail;
    store.role = auth.user.role;

    const [employeeRes, partnerRes] = await Promise.all([
      employeeAPI.getEmployee(auth.sub),
      partnerAPI.getPartnerByEmployee(auth.sub),
    ]);

    const employee = employeeRes.data?.[0];
    const partner = partnerRes.data?.[0]?.headquarters?.[0]?.partner;
    if (!employee || !partner) throw authError('NOT_AUTHORIZED');

    const landing = landingRouteFor(auth.user.role, partner);
    if (!landing) throw authError('NOT_AUTHORIZED');

    store.employee = toRaw(employee);
    store.partner = partner;
    store.bookingType = partner.booking_type === true;
    store.journeyType = partner.journey_type === true;
    store.restaurantType = partner.is_restaurant === true;
    store.isLogged = true;
    store.viewMenu = true;

    return { role: auth.user.role, partner, landing };
  } catch (err) {
    clearSession();
    if (err.isAuthError) throw err;
    const status = err?.response?.status;
    if (status === 401 || status === 404 || status === 400) throw authError('INVALID_CREDENTIALS', err);
    if (!err?.response) throw authError('NETWORK', err);
    throw authError('UNKNOWN', err);
  }
}

export const AUTH_ERROR_MESSAGES = {
  INVALID_CREDENTIALS: 'Username o password errati',
  NOT_AUTHORIZED: 'Account non abilitato al gestionale partner',
  NETWORK: 'Impossibile contattare il server: controlla la connessione',
  UNKNOWN: 'Errore inatteso durante il login',
};
